import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DiscountIcon from '@mui/icons-material/LocalOffer';
import ViewIcon from '@mui/icons-material/Visibility';
import ClickIcon from '@mui/icons-material/Mouse';
import RevenueIcon from '@mui/icons-material/AttachMoney';
import ProductIcon from '@mui/icons-material/Inventory';
import ShopIcon from '@mui/icons-material/Store';
import { useShop } from '../../context/ShopContext';
import { supabase } from '../../services/supabaseClient';
import { NotificationService } from '../../services/errorService';
import { format } from 'date-fns';

const CampaignManager: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const { shops } = useShop();
  const [shop, setShop] = useState<any>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  // New Campaign Form State
  const [formData, setFormData] = useState({
    shop_id: shopId || '',
    holiday_name: '',
    campaign_type: 'holiday',
    target_type: 'all_products',
    target_ids: [] as string[],
    discount_percent: 10,
    start_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    end_date: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd'T'HH:mm"),
    description: '',
    coupon_code: '',
  });

  useEffect(() => {
    const currentShop = shops.find(s => s.id === (formData.shop_id || shopId));
    if (currentShop) {
      setShop(currentShop);
      fetchCampaigns(currentShop.id);
      fetchProducts(currentShop.id);
    } else if (shops.length > 0) {
      // Default to first shop if none selected
      setFormData(prev => ({ ...prev, shop_id: shops[0].id }));
    }
  }, [shopId, shops, formData.shop_id, navigate]);

  const fetchCampaigns = async (id: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from('shop_promotions')
      .select(`
        *,
        campaign_analytics (*)
      `)
      .eq('shop_id', id)
      .order('created_at', { ascending: false });

    if (error) {
      NotificationService.error('Error', 'Failed to fetch campaigns');
    } else {
      setCampaigns(data || []);
    }
    setLoading(false);
  };

  const fetchProducts = async (id: string) => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('shop_id', id);

    if (!error) {
      setProducts(data || []);
    }
  };

  const handleCreateCampaign = async () => {
    if (!formData.shop_id) return;
    setSaving(true);
    
    const { data, error } = await supabase
      .from('shop_promotions')
      .insert([{
        ...formData,
        is_active: true
      }])
      .select()
      .single();

    if (error) {
      NotificationService.error('Failed', error.message);
    } else {
      NotificationService.success('Success', 'Campaign created successfully!');
      if (formData.shop_id === shop?.id) {
        setCampaigns([data, ...campaigns]);
      }
      setOpenDialog(false);
      setFormData({
        shop_id: shop?.id || '',
        holiday_name: '',
        campaign_type: 'holiday',
        target_type: 'all_products',
        target_ids: [],
        discount_percent: 10,
        start_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        end_date: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd'T'HH:mm"),
        description: '',
        coupon_code: '',
      });
    }
    setSaving(false);
  };

  const getStatus = (campaign: any) => {
    const now = new Date();
    const start = new Date(campaign.start_date);
    const end = new Date(campaign.end_date);

    if (!campaign.is_active) return { label: 'Inactive', color: 'default' };
    if (now < start) return { label: 'Scheduled', color: 'info' };
    if (now > end) return { label: 'Expired', color: 'error' };
    return { label: 'Active', color: 'success' };
  };

  if (loading && !shop) return <CircularProgress />;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Campaign Manager</Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your holiday discounts and marketing campaigns for <strong>{shop?.name}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ borderRadius: '12px', px: 3 }}
        >
          Create Campaign
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* Performance Overview */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: 'primary.dark', color: 'white' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="h6">Total Campaign Revenue</Typography>
                <Typography variant="h3" fontWeight="bold">$0.00</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Across {campaigns.length} campaigns</Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={4} justifyContent="flex-end">
                  <Box textAlign="center">
                    <ViewIcon sx={{ mb: 1 }} />
                    <Typography variant="h6">0</Typography>
                    <Typography variant="caption">Views</Typography>
                  </Box>
                  <Box textAlign="center">
                    <ClickIcon sx={{ mb: 1 }} />
                    <Typography variant="h6">0</Typography>
                    <Typography variant="caption">Clicks</Typography>
                  </Box>
                  <Box textAlign="center">
                    <TrendingUpIcon sx={{ mb: 1 }} />
                    <Typography variant="h6">0%</Typography>
                    <Typography variant="caption">Conversion</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Campaign List */}
        {campaigns.length === 0 ? (
          <Grid item xs={12}>
            <Card sx={{ textAlign: 'center', py: 8, borderRadius: '16px', bgcolor: 'background.paper', border: '1px dashed grey' }}>
              <DiscountIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No active campaigns</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Start driving sales by creating your first discount campaign.</Typography>
              <Button variant="outlined" onClick={() => setOpenDialog(true)}>Setup Holiday Sale</Button>
            </Card>
          </Grid>
        ) : (
          campaigns.map((campaign) => {
            const status = getStatus(campaign);
            const analytics = campaign.campaign_analytics?.[0] || { views: 0, clicks: 0, revenue_generated: 0 };
            
            return (
              <Grid item xs={12} md={6} key={campaign.id}>
                <Card sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">{campaign.holiday_name}</Typography>
                        <Chip size="small" label={campaign.campaign_type.replace('_', ' ')} sx={{ textTransform: 'capitalize', mr: 1 }} />
                        <Chip size="small" label={status.label} color={status.color as any} />
                      </Box>
                      <Typography variant="h5" color="primary.main" fontWeight="bold">-{campaign.discount_percent}%</Typography>
                    </Stack>

                    <Stack spacing={1} mb={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {format(new Date(campaign.start_date), 'MMM d, p')} - {format(new Date(campaign.end_date), 'MMM d, p')}
                        </Typography>
                      </Box>
                      {campaign.coupon_code && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DiscountIcon fontSize="small" color="action" />
                          <Typography variant="body2">Code: <strong>{campaign.coupon_code}</strong></Typography>
                        </Box>
                      )}
                    </Stack>

                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: '12px' }}>
                      <Typography variant="subtitle2" gutterBottom>Performance</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Views</Typography>
                          <Typography variant="body1" fontWeight="bold">{analytics.views}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Clicks</Typography>
                          <Typography variant="body1" fontWeight="bold">{analytics.clicks}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Revenue</Typography>
                          <Typography variant="body1" fontWeight="bold" color="success.main">${analytics.revenue_generated}</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Stack direction="row" spacing={1} mt={3} justifyContent="flex-end">
                      <IconButton size="small"><EditIcon /></IconButton>
                      <IconButton size="small" color="error"><DeleteIcon /></IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* Create Campaign Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Marketing Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              select
              label="Select Shop"
              value={formData.shop_id}
              onChange={(e) => setFormData({ ...formData, shop_id: e.target.value })}
              InputProps={{
                startAdornment: <ShopIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            >
              {shops.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Campaign Name (e.g. Christmas Sale)"
              value={formData.holiday_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, holiday_name: e.target.value })}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Campaign Type"
                  value={formData.campaign_type}
                  onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value })}
                >
                  <MenuItem value="holiday">Holiday</MenuItem>
                  <MenuItem value="flash_sale">Flash Sale</MenuItem>
                  <MenuItem value="clearance">Clearance</MenuItem>
                  <MenuItem value="new_arrival">New Arrival</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount %"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) })}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Apply To"
                  value={formData.target_type}
                  onChange={(e) => setFormData({ ...formData, target_type: e.target.value, target_ids: [] })}
                >
                  <MenuItem value="all_products">All Products</MenuItem>
                  <MenuItem value="specific_products">Specific Products</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Coupon Code (Optional)"
                  value={formData.coupon_code}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })}
                  placeholder="e.g. HOLIDAY20"
                />
              </Grid>
            </Grid>

            {formData.target_type === 'specific_products' && (
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ProductIcon fontSize="small" /> Select Products ({formData.target_ids.length} selected)
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                  {products.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      No products found for this shop.
                    </Typography>
                  ) : (
                    <Grid container spacing={1}>
                      {products.map((p) => (
                        <Grid item xs={12} key={p.id}>
                          <Chip
                            label={p.name}
                            onClick={() => {
                              const isSelected = formData.target_ids.includes(p.id);
                              setFormData({
                                ...formData,
                                target_ids: isSelected
                                  ? formData.target_ids.filter(id => id !== p.id)
                                  : [...formData.target_ids, p.id]
                              });
                            }}
                            color={formData.target_ids.includes(p.id) ? "primary" : "default"}
                            variant={formData.target_ids.includes(p.id) ? "filled" : "outlined"}
                            sx={{ width: '100%', justifyContent: 'flex-start' }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start Date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="End Date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCampaign}
            disabled={saving || !formData.holiday_name || (formData.target_type === 'specific_products' && formData.target_ids.length === 0)}
          >
            {saving ? <CircularProgress size={24} /> : 'Launch Campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignManager;
